/**
 * Техника, топливо и выработка (разделы 6.32 и 6.33 ТЗ, поток 9): карточка единицы поверх парка графика,
 * документы со сроками, наряды и путевые листы с подписями, табель, топливо против нормы, стоимость
 * машино-часа и списание, сверка выработки (КП-01). Суммы — по уровню денег «Акты» (ДС-04).
 */
import { z } from 'zod';

export const UNIT_KINDS = {
  excavator: 'Экскаватор',
  loader: 'Погрузчик',
  bulldozer: 'Бульдозер',
  crane: 'Кран',
  truck: 'Самосвал, грузовик',
  roller: 'Каток',
  concrete: 'Бетононасос, миксер',
  generator: 'Генератор, компрессор',
  other: 'Другая',
} as const;
export type UnitKind = keyof typeof UNIT_KINDS;
export const UnitKindEnum = z.enum(Object.keys(UNIT_KINDS) as [UnitKind, ...UnitKind[]]);

export const UNIT_STATES = {
  working: 'В работе',
  maintenance: 'ТО',
  repair: 'Ремонт',
  idle: 'Простой',
  transit: 'В пути',
} as const;
export type UnitState = keyof typeof UNIT_STATES;
export const UnitStateEnum = z.enum(Object.keys(UNIT_STATES) as [UnitState, ...UnitState[]]);

export const UNIT_DOC_KINDS = {
  insurance: 'Страховка',
  inspection: 'Техосмотр',
  permit: 'Допуск, разрешение',
  registration: 'Регистрация',
  license: 'Лицензия, аттестат',
  other: 'Другое',
} as const;
export type UnitDocKind = keyof typeof UNIT_DOC_KINDS;
export const UnitDocKindEnum = z.enum(Object.keys(UNIT_DOC_KINDS) as [UnitDocKind, ...UnitDocKind[]]);

/** За сколько дней до окончания документа портал напоминает (как КА-04) */
export const UNIT_DOC_REMIND_DAYS = 30;

export const FUEL_SOURCES = {
  station: 'АЗС (чек, карта)',
  bowser: 'Топливозаправщик на площадке',
  depot: 'Склад ГСМ',
} as const;
export type FuelSource = keyof typeof FUEL_SOURCES;
export const FuelSourceEnum = z.enum(Object.keys(FUEL_SOURCES) as [FuelSource, ...FuelSource[]]);

export const WAYBILL_STATUSES = {
  draft: 'Черновик',
  operator: 'Подписал машинист',
  signed: 'Подписан прорабом',
  cancelled: 'Отменён',
} as const;
export type WaybillStatus = keyof typeof WAYBILL_STATUSES;

export const WORK_ORDER_STATUSES = {
  issued: 'Выдан',
  closed: 'Закрыт путевым листом',
  cancelled: 'Отменён',
} as const;

const Day = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Дата в виде ГГГГ-ММ-ДД')
  .refine((v) => {
    const d = new Date(`${v}T00:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
  }, 'Такой даты нет в календаре');
const Hours = z.number().min(0).max(24);
const Meter = z.number().min(0).max(99_999_999);
const Opt = <T extends z.ZodType>(t: T) => t.nullable().optional();

export const UnitDocRow = z.object({
  id: z.string(),
  machineId: z.string(),
  kind: UnitDocKindEnum,
  number: z.string().nullable(),
  issuedOn: z.string().nullable(),
  validTo: z.string().nullable(),
  note: z.string().nullable(),
  /** Дней до окончания (отрицательное — просрочен); пусто — бессрочный */
  daysLeft: z.number().int().nullable(),
  state: z.enum(['ok', 'soon', 'expired', 'open']),
});
export type UnitDocRow = z.infer<typeof UnitDocRow>;

export const UnitDocInput = z.object({
  kind: UnitDocKindEnum,
  number: Opt(z.string().trim().max(120)),
  issuedOn: Opt(Day),
  validTo: Opt(Day),
  note: Opt(z.string().trim().max(1000)),
});
export type UnitDocInput = z.infer<typeof UnitDocInput>;

export const UnitCardInput = z.object({
  kind: UnitKindEnum.optional(),
  model: Opt(z.string().trim().max(200)),
  year: Opt(z.number().int().min(1950).max(2100)),
  inventoryNo: Opt(z.string().trim().max(120)),
  contractId: Opt(z.uuid()),
  objectId: Opt(z.uuid()),
  state: UnitStateEnum.optional(),
  operatorId: Opt(z.uuid()),
  mechanicId: Opt(z.uuid()),
  amortPerHour: Opt(z.number().min(0).max(1e9)),
  rentPerHour: Opt(z.number().min(0).max(1e9)),
  operatorPerHour: Opt(z.number().min(0).max(1e9)),
  servicePerHour: Opt(z.number().min(0).max(1e9)),
  fuelIdleNorm: Opt(z.number().min(0).max(1000)),
  fuelTolerancePct: z.number().min(0).max(100).optional(),
  note: Opt(z.string().trim().max(2000)),
});
export type UnitCardInput = z.infer<typeof UnitCardInput>;

/** Новая единица техники: машина парка графика и её карточка; проверяет лимит тарифа (ЛЦ-04) */
export const UnitCreateInput = UnitCardInput.extend({
  resourceId: z.uuid(),
  name: z.string().trim().min(1).max(200),
  regNumber: Opt(z.string().trim().max(40)),
  serialNumber: Opt(z.string().trim().max(60)),
  ownership: z.enum(['own', 'lease', 'rent']).default('own'),
  ownerId: Opt(z.uuid()),
  projectId: Opt(z.uuid()),
  fuelNorm: Opt(z.number().min(0).max(1000)),
});
export type UnitCreateInput = z.infer<typeof UnitCreateInput>;

export const UnitRow = z.object({
  machineId: z.string(),
  name: z.string(),
  regNumber: z.string().nullable(),
  ownership: z.enum(['own', 'lease', 'rent']),
  ownerName: z.string().nullable(),
  projectId: z.string().nullable(),
  projectCode: z.string().nullable(),
  machineStatus: z.enum(['active', 'repair', 'off']),
  kind: UnitKindEnum,
  model: z.string().nullable(),
  year: z.number().nullable(),
  inventoryNo: z.string().nullable(),
  contractId: z.string().nullable(),
  objectId: z.string().nullable(),
  state: UnitStateEnum,
  operatorId: z.string().nullable(),
  operatorName: z.string().nullable(),
  mechanicId: z.string().nullable(),
  hourMeter: z.number().nullable(),
  fuelNorm: z.number().nullable(),
  fuelIdleNorm: z.number().nullable(),
  fuelTolerancePct: z.number(),
  /** Расходы на час — только с уровнем денег «Акты» */
  amortPerHour: z.number().nullable(),
  rentPerHour: z.number().nullable(),
  operatorPerHour: z.number().nullable(),
  servicePerHour: z.number().nullable(),
  note: z.string().nullable(),
  docs: z.array(UnitDocRow),
  /** Худшее состояние документов: expired, soon, ok, open (нет документов со сроком) */
  docsState: z.enum(['expired', 'soon', 'ok', 'open']),
  hasCard: z.boolean(),
});
export type UnitRow = z.infer<typeof UnitRow>;

export const WorkOrderInput = z.object({
  projectId: z.uuid(),
  machineId: z.uuid(),
  day: Day,
  activityId: Opt(z.uuid()),
  zone: Opt(z.string().trim().max(200)),
  operatorId: Opt(z.uuid()),
  plannedHours: z.number().min(0).max(24).default(8),
  note: Opt(z.string().trim().max(1000)),
});
export type WorkOrderInput = z.infer<typeof WorkOrderInput>;

export const WorkOrderRow = z.object({
  id: z.string(),
  projectId: z.string(),
  projectCode: z.string().nullable(),
  machineId: z.string(),
  machineName: z.string(),
  regNumber: z.string().nullable(),
  day: z.string(),
  activityId: z.string().nullable(),
  activityName: z.string().nullable(),
  zone: z.string().nullable(),
  operatorId: z.string().nullable(),
  operatorName: z.string().nullable(),
  plannedHours: z.number(),
  source: z.enum(['manual', 'schedule', 'site']),
  status: z.enum(['issued', 'closed', 'cancelled']),
  waybillId: z.string().nullable(),
  note: z.string().nullable(),
});
export type WorkOrderRow = z.infer<typeof WorkOrderRow>;

/** Путевой лист: создаётся и правится с телефона; clientOpId не даёт задвоить при повторной отправке */
export const WaybillInput = z.object({
  clientOpId: z.uuid().optional(),
  projectId: z.uuid(),
  machineId: z.uuid(),
  workOrderId: Opt(z.uuid()),
  day: Day,
  activityId: Opt(z.uuid()),
  zone: Opt(z.string().trim().max(200)),
  operatorId: Opt(z.uuid()),
  hourStart: Opt(Meter),
  hourEnd: Opt(Meter),
  odoStart: Opt(Meter),
  odoEnd: Opt(Meter),
  idleHours: Hours.default(0),
  idleReason: Opt(z.enum(['repair', 'no_front', 'no_operator', 'no_fuel', 'weather', 'other'])),
  fuelStart: Opt(z.number().min(0).max(100_000)),
  fuelEnd: Opt(z.number().min(0).max(100_000)),
  note: Opt(z.string().trim().max(1000)),
});
export type WaybillInput = z.infer<typeof WaybillInput>;
export const WaybillPatch = WaybillInput.omit({
  clientOpId: true,
  projectId: true,
  machineId: true,
  day: true,
  idleHours: true,
})
  .partial()
  // Без значения по умолчанию: правка заметки не должна обнулять простой
  .extend({ idleHours: Hours.optional() });
export type WaybillPatch = z.infer<typeof WaybillPatch>;

export const WaybillRow = z.object({
  id: z.string(),
  number: z.string(),
  projectId: z.string(),
  projectCode: z.string().nullable(),
  machineId: z.string(),
  machineName: z.string(),
  regNumber: z.string().nullable(),
  workOrderId: z.string().nullable(),
  day: z.string(),
  activityId: z.string().nullable(),
  activityName: z.string().nullable(),
  zone: z.string().nullable(),
  operatorId: z.string().nullable(),
  operatorName: z.string().nullable(),
  hourStart: z.number().nullable(),
  hourEnd: z.number().nullable(),
  odoStart: z.number().nullable(),
  odoEnd: z.number().nullable(),
  /** Отработано по моточасам и пробег за смену */
  workHours: z.number().nullable(),
  distanceKm: z.number().nullable(),
  idleHours: z.number(),
  idleReason: z.string().nullable(),
  fuelStart: z.number().nullable(),
  fuelEnd: z.number().nullable(),
  /** Заправки машины в этот день */
  fuelFilled: z.number(),
  /** Расход за смену: остаток на начало + заправки − остаток на конец */
  fuelUsed: z.number().nullable(),
  status: z.enum(['draft', 'operator', 'signed', 'cancelled']),
  operatorSignedAt: z.string().nullable(),
  foremanSignedAt: z.string().nullable(),
  /** Расхождение с телематикой (РС-16), если показания есть */
  mismatch: z.array(z.string()),
  note: z.string().nullable(),
  /** Что пользователь может сделать с листом сейчас */
  actions: z.array(z.enum(['edit', 'sign_operator', 'sign_foreman', 'cancel'])),
});
export type WaybillRow = z.infer<typeof WaybillRow>;

export const WaybillSignInput = z.object({ as: z.enum(['operator', 'foreman']) });

export const FleetTimesheetDay = z.object({
  day: z.string(),
  work: z.number(),
  idle: z.number(),
  repair: z.number(),
});
export const FleetTimesheetRow = z.object({
  machineId: z.string(),
  name: z.string(),
  regNumber: z.string().nullable(),
  projectCode: z.string().nullable(),
  workHours: z.number(),
  idleHours: z.number(),
  repairHours: z.number(),
  /** Часы простоя по причинам: нет фронта, нет топлива, нет машиниста, погода, ремонт, другое */
  idleByReason: z.record(z.string(), z.number()),
  /** Загрузка = отработанные часы / (дни периода × смена) */
  loadPct: z.number(),
  days: z.array(FleetTimesheetDay),
});
export const FleetTimesheet = z.object({
  from: z.string(),
  to: z.string(),
  shiftHours: z.number(),
  rows: z.array(FleetTimesheetRow),
  /** Загрузка парка по дням, % */
  fleetLoad: z.array(z.object({ day: z.string(), pct: z.number() })),
});
export type FleetTimesheet = z.infer<typeof FleetTimesheet>;

export const FuelFillInput = z.object({
  machineId: z.uuid(),
  projectId: Opt(z.uuid()),
  day: Day,
  litres: z.number().positive().max(100_000),
  pricePerLitre: Opt(z.number().min(0).max(1e6)),
  source: FuelSourceEnum.default('station'),
  cardNo: Opt(z.string().trim().max(60)),
  receiptNo: Opt(z.string().trim().max(60)),
  hourMeter: Opt(Meter),
  note: Opt(z.string().trim().max(1000)),
});
export type FuelFillInput = z.infer<typeof FuelFillInput>;

export const FuelFillRow = z.object({
  id: z.string(),
  machineId: z.string(),
  machineName: z.string(),
  regNumber: z.string().nullable(),
  projectId: z.string().nullable(),
  projectCode: z.string().nullable(),
  day: z.string(),
  litres: z.number(),
  pricePerLitre: z.number().nullable(),
  amount: z.number().nullable(),
  source: FuelSourceEnum,
  cardNo: z.string().nullable(),
  receiptNo: z.string().nullable(),
  hourMeter: z.number().nullable(),
  note: z.string().nullable(),
});
export type FuelFillRow = z.infer<typeof FuelFillRow>;

export const FuelNormRow = z.object({
  machineId: z.string(),
  name: z.string(),
  regNumber: z.string().nullable(),
  projectCode: z.string().nullable(),
  workHours: z.number(),
  idleHours: z.number(),
  normPerHour: z.number().nullable(),
  idleNorm: z.number().nullable(),
  tolerancePct: z.number(),
  /** Расход по норме и фактический (по путевым листам, а где их нет — по заправкам) */
  normLitres: z.number().nullable(),
  factLitres: z.number(),
  factBasis: z.enum(['waybills', 'fills']),
  filledLitres: z.number(),
  deviationPct: z.number().nullable(),
  over: z.boolean(),
  amount: z.number().nullable(),
});
export const FuelReport = z.object({ from: z.string(), to: z.string(), rows: z.array(FuelNormRow) });
export type FuelReport = z.infer<typeof FuelReport>;

export const HourRate = z.object({
  machineId: z.string(),
  day: z.string(),
  /** Плановая цена часа из справочника ресурсов (ставка вида техники) для сравнения со сметой */
  catalogRate: z.number().nullable(),
  planRate: z.number().nullable(),
  /** Составляющие плана: амортизация, аренда, машинист, ТО */
  plan: z.object({
    amort: z.number(),
    rent: z.number(),
    operator: z.number(),
    service: z.number(),
  }),
  factRate: z.number().nullable(),
  fact: z.object({
    amort: z.number(),
    rent: z.number(),
    operator: z.number(),
    service: z.number(),
    fuel: z.number(),
  }),
  /** Часы, по которым посчитан факт за 30 дней до дня */
  hours: z.number(),
});
export type HourRate = z.infer<typeof HourRate>;

export const CostRow = z.object({
  machineId: z.string(),
  name: z.string(),
  regNumber: z.string().nullable(),
  projectId: z.string().nullable(),
  projectCode: z.string().nullable(),
  hours: z.number(),
  amount: z.number().nullable(),
  avgRate: z.number().nullable(),
  planRate: z.number().nullable(),
  fuelAmount: z.number().nullable(),
  serviceAmount: z.number().nullable(),
  /** Отклонение факта от плана по часу, % */
  deviationPct: z.number().nullable(),
});
export const CostReport = z.object({
  from: z.string(),
  to: z.string(),
  rows: z.array(CostRow),
  total: z.number().nullable(),
});
export type CostReport = z.infer<typeof CostReport>;

export const CostPostingRow = z.object({
  id: z.string(),
  waybillId: z.string(),
  waybillNumber: z.string(),
  machineId: z.string(),
  machineName: z.string(),
  activityId: z.string().nullable(),
  activityName: z.string().nullable(),
  budgetLineId: z.string().nullable(),
  day: z.string(),
  hours: z.number(),
  rate: z.number().nullable(),
  amount: z.number().nullable(),
});
export type CostPostingRow = z.infer<typeof CostPostingRow>;

export const OutputNormInput = z.object({
  name: z.string().trim().min(1).max(200),
  unit: z.string().trim().min(1).max(30),
  basis: z.enum(['machine', 'labor']).default('machine'),
  perHour: z.number().positive().max(1e9),
  source: Opt(z.string().trim().max(120)),
  note: Opt(z.string().trim().max(1000)),
});
export type OutputNormInput = z.infer<typeof OutputNormInput>;
export const OutputNormRow = OutputNormInput.extend({ id: z.string() }).extend({
  basis: z.enum(['machine', 'labor']),
  source: z.string().nullable(),
  note: z.string().nullable(),
});
export type OutputNormRow = z.infer<typeof OutputNormRow>;

export const OutputRow = z.object({
  activityId: z.string(),
  code: z.string(),
  name: z.string(),
  unit: z.string().nullable(),
  /** Объём факта по работе (из отчёта смены, принятый в графике) */
  quantity: z.number().nullable(),
  machineHours: z.number(),
  laborHours: z.number().nullable(),
  /** Выработка факта и норма на машино-час; норма — ближайшая по названию и единице */
  factPerHour: z.number().nullable(),
  normPerHour: z.number().nullable(),
  normName: z.string().nullable(),
  deviationPct: z.number().nullable(),
  /** Прогноз срока: сколько машино-часов осталось по текущей выработке */
  remainingQuantity: z.number().nullable(),
  forecastHoursLeft: z.number().nullable(),
  /** Стоимость единицы объёма по факту машино-часов */
  unitCost: z.number().nullable(),
});
export const OutputReport = z.object({ from: z.string(), to: z.string(), rows: z.array(OutputRow) });
export type OutputReport = z.infer<typeof OutputReport>;

/* ── ТО по моточасам (ТХ-03) ── */

export const SERVICE_STATES = ['overdue', 'soon', 'ok', 'unknown'] as const;
export type ServiceState = (typeof SERVICE_STATES)[number];
export const SERVICE_STATE_LABELS: Record<ServiceState, string> = {
  overdue: 'Просрочено',
  soon: 'Скоро',
  ok: 'В норме',
  unknown: 'Нет отметки',
};

/** Регламент ТО: интервал в моточасах, в днях или оба — срабатывает то, что наступит раньше */
export const ServiceRuleInput = z
  .object({
    /** Модель из карточки единицы; пусто — для всех единиц компании */
    model: z.string().trim().max(120).nullable().optional(),
    name: z.string().trim().min(1).max(120),
    everyHours: z.number().gt(0).max(100000).nullable().optional(),
    everyDays: z.number().int().min(1).max(3660).nullable().optional(),
    partsHint: z.string().trim().max(2000).nullable().optional(),
    note: z.string().trim().max(1000).nullable().optional(),
  })
  .refine((v) => v.everyHours != null || v.everyDays != null, 'Укажите интервал в моточасах или в днях');
export type ServiceRuleInput = z.infer<typeof ServiceRuleInput>;

export const ServiceRuleRow = z.object({
  id: z.string(),
  model: z.string().nullable(),
  name: z.string(),
  everyHours: z.number().nullable(),
  everyDays: z.number().nullable(),
  partsHint: z.string().nullable(),
  note: z.string().nullable(),
  /** Сколько единиц подпадает под правило */
  units: z.number(),
});
export type ServiceRuleRow = z.infer<typeof ServiceRuleRow>;

/** Отметка выполненного ТО по регламенту; в журнал ТО машины попадает тоже */
export const ServiceMarkInput = z.object({
  ruleId: z.uuid(),
  day: Day,
  hourMeter: z.number().min(0).max(1e7).nullable().optional(),
  cost: z.number().min(0).max(999_999_999_999).nullable().optional(),
  contractorId: z.uuid().nullable().optional(),
  note: z.string().trim().max(1000).nullable().optional(),
});
export type ServiceMarkInput = z.infer<typeof ServiceMarkInput>;

export const ServiceItem = z.object({
  ruleId: z.string(),
  ruleName: z.string(),
  everyHours: z.number().nullable(),
  everyDays: z.number().nullable(),
  /** Последнее ТО по этому регламенту (или общая отметка машины, если по регламенту ещё не отмечали) */
  lastDay: z.string().nullable(),
  lastHours: z.number().nullable(),
  /** Моточасы следующего ТО и сколько осталось (отрицательное — перепробег) */
  nextHours: z.number().nullable(),
  hoursLeft: z.number().nullable(),
  /** Ожидаемая дата: по наработке в день или по календарному интервалу — что раньше */
  nextDay: z.string().nullable(),
  basis: z.enum(['hours', 'days', 'none']),
  state: z.enum(SERVICE_STATES),
  /** Отметки на шкале наработки: последние ТО по этому регламенту */
  marks: z.array(z.object({ day: z.string(), hourMeter: z.number().nullable() })),
  /** Ожидаемые даты следующих ТО на 12 месяцев вперёд (для плана «единица × месяц») */
  forecast: z.array(z.string()),
  partsRequest: z.object({ id: z.string(), number: z.number(), stage: z.string() }).nullable(),
});
export type ServiceItem = z.infer<typeof ServiceItem>;

export const ServicePlanUnit = z.object({
  machineId: z.string(),
  name: z.string(),
  regNumber: z.string().nullable(),
  model: z.string().nullable(),
  projectId: z.string().nullable(),
  projectCode: z.string().nullable(),
  hourMeter: z.number().nullable(),
  /** Средняя наработка за последние 30 дней, моточасов в день, по путевым листам */
  perDay: z.number(),
  items: z.array(ServiceItem),
});
export type ServicePlanUnit = z.infer<typeof ServicePlanUnit>;

export const ServicePlan = z.object({
  today: z.string(),
  /** 12 месяцев вперёд от текущего: ГГГГ-ММ */
  months: z.array(z.string()),
  units: z.array(ServicePlanUnit),
});
export type ServicePlan = z.infer<typeof ServicePlan>;

export const PartsRequestInput = z.object({
  ruleId: z.uuid().nullable().optional(),
  requiredDate: Day.nullable().optional(),
  note: z.string().trim().max(1000).nullable().optional(),
  lines: z
    .array(
      z.object({
        name: z.string().trim().min(1).max(300),
        unit: z.string().trim().min(1).max(20),
        quantity: z.number().gt(0).max(1e9),
      }),
    )
    .min(1)
    .max(50),
});
export type PartsRequestInput = z.infer<typeof PartsRequestInput>;
